import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Item } from '@app/core/components/listbox/listbox.component';

@Injectable({
    providedIn: 'root',
})
export class ConsignmentService {
    constructor(private datatask: DataTaskService) {}

    public async getConsignment(params: ConsignmentAssignParams): Promise<ConsignmentGet | undefined> {
        return (await this.datatask.Post(322, params))[0];
    }

    public async saveConsignment(params: ConsignmentGet): Promise<ConsignmentSaveResponse | undefined> {
        return (await this.datatask.Post(413, params))[0];
    }

    public getOrderLines(params: ConsignmentAssignParams): Promise<Item[]> { return this.datatask.Post(326, params); }
    public getPickLines(params: ConsignmentAssignParams): Promise<Item[]> { return this.datatask.Post(327, params); }
    public getManualPickLines(params: ConsignmentAssignParams): Promise<Item[]> { return this.datatask.Post(615, params); }
    public getDeliveryMethods(params: ConsignmentAssignParams): Promise<ConsignmentDeliveryMethod[]> { return this.datatask.Post(399, params); }
    public getPrePickLines(params: ConsignmentStockTransferParams): Promise<ConsignmentPrePickItem[]> { return this.datatask.Post(1055, params); }
    public async finishPrePick(params: ConsignmentStockTransferParams): Promise<ErrorMessageResponse> { return (await this.datatask.Post(1064, params))[0]; }
    public async selectPickLines(records: Item[]): Promise<ConsignmentPickResponse> { return (await this.datatask.Post(755, { records }))[0]; }
    public getCollis(params: ConsignmentColliListParams): Promise<ConsignmentColliItem[]> { return this.datatask.Post(439, params); }
    public mergeCollis(params: ConsignmentColliActionParams): Promise<unknown> { return this.datatask.Post(1428, params); }
    public splitCollis(params: ConsignmentColliActionParams): Promise<unknown> { return this.datatask.Post(1430, params); }
    public async prevalidate(params: ConsignmentAssignParams): Promise<ConsignmentPrevalidation> { return (await this.datatask.Post(1575, params))[0]; }
    public completeWebshop(params: ConsignmentAssignParams): Promise<unknown> { return this.datatask.Post(1437, params); }
    public async checkDelete(params: ConsignmentAssignParams): Promise<ErrorMessageResponse> { return (await this.datatask.Post(3768, params))[0]; }
    public deleteConsignment(params: ConsignmentAssignParams): Promise<unknown> { return this.datatask.Post(758, params); }
    public async rollback(params: ConsignmentAssignParams): Promise<ErrorMessageResponse> { return (await this.datatask.Post(2253, params))[0]; }
    public async estimateFreight(params: ConsignmentFreightParams): Promise<ConsignmentFreightResponse> { return (await this.datatask.Post(350, params))[0]; }
    public applyFreight(params: ConsignmentApplyFreightParams): Promise<{ iserror?: boolean }> { return this.datatask.Post(3195, params); }
    public async getOrderFreight(params: ConsignmentOrderFreightParams): Promise<ConsignmentOrderFreightResponse | undefined> { return (await this.datatask.Post(3199, params))[0]; }

    public async getLineAssignment(params: ConsignmentLineAssignParams): Promise<ConsignmentLineAssignGet | undefined> {
        return (await this.datatask.Post(3565, params))[0];
    }

    public async saveLineAssignment(params: ConsignmentLineAssignSaveParams): Promise<ErrorMessageResponse | undefined> {
        return (await this.datatask.Post(3567, params))[0];
    }

    public getSerialNumberLines(params: ConsignmentSerialNumberParams): Promise<ConsignmentSerialNumberLine[]> {
        return this.datatask.Post(3196, params);
    }

    public async saveSerialNumbers(records: ConsignmentSerialNumberLine[]): Promise<ErrorMessageResponse | undefined> {
        return (await this.datatask.Post(3197, { records }))[0];
    }

    public async getConsignments(params: ConsignmentsParams): Promise<ConsignmentsGet | undefined> {
        return (await this.datatask.Post(831, params))[0];
    }

    public async getCompanyChanged(params: ConsignmentsCompanyParams): Promise<ConsignmentsCompanyChanged | undefined> {
        return (await this.datatask.Post(1090, params))[0];
    }

    public async getStockplaceChanged(params: ConsignmentsStockplaceParams): Promise<ConsignmentsStockplaceChanged | undefined> {
        return (await this.datatask.Post(1094, params))[0];
    }

    public listConsignments(params: ConsignmentsListParams): Promise<Item[]> {
        return this.datatask.Post(329, params);
    }

    public getConsignmentShipments(params: ConsignmentShipmentsParams): Promise<Item[]> {
        return this.datatask.Post(1107, params);
    }

    /**
     * @datatask 415
     * @datataskName sp_ws_p2_consignment_assign_sb
     * @param params consignment keyno to assign to.
     * @returns promise containing validation response.
     */
    public async assign(params: ConsignmentAssignParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(415, params))[0];
    }

    /**
     * @datatask 3557
     * @datataskName sp_ws_p2_consignmentline_assign_delete
     * @param params the consignment line keyno to delete consignment assignment from.
     * @returns promise containing validation response.
     */
    public async deleteLineAssignment(params: DeleteConsignmentLineAssignmentParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3557, params))[0];
    }

    /**
     * @datatask 418
     * @datataskName sp_ws_p2_consignment_delivery
     * @param params the consignment to confirm delivery on.
     * @returns promise containing validation response.
     */
    public async confirmDelivery(params: ConsignmentConfirmDeliveryParams): Promise<ConsignmentConfirmDeliveryResponse> {
        return (await this.datatask.Post(418, params))[0];
    }
}

export interface ConsignmentAssignParams {
    consignment_keyno: string | number;
}

export interface ConsignmentLineAssignParams {
    consignmentline_keyno: string | number;
}

export interface ConsignmentLineAssignGet extends Record<string, unknown> {
    prod_id: string;
    prod_name: string;
    quantity_topick: string | number;
    quantity_picked: string | number;
    quantity_rest: string | number;
}

export interface ConsignmentLineAssignRow extends Record<string, unknown> {
    quantity_pick_now: string | number;
}

export interface ConsignmentLineAssignSaveParams extends ConsignmentLineAssignGet {
    records: ConsignmentLineAssignRow[];
}

export interface ConsignmentSerialNumberParams {
    consignment_keyno: string | number;
}

export interface ConsignmentSerialNumberLine extends Record<string, unknown> {
    prod_name: string;
    serialno: string;
}

export interface DeleteConsignmentLineAssignmentParams {
    consignmentline_keyno: string | number;
}

export interface ConsignmentConfirmDeliveryParams {
    consignment_keyno: string | number;
}

export interface ConsignmentConfirmDeliveryResponse {
    statusid: string;
    validationcode: string;
    validationmessage: string;
}

export interface ConsignmentsParams {
    argtype: string;
    argvalue: string;
}

export interface ConsignmentsGet {
    businessco_no_from: string;
    stockplace_id_from: string;
    stocklocation_keyno_trolley: string;
    consignmentshipment_keyno: string;
    date_fom: string;
    date_tom: string;
    dateselector_index: string;
    gb_consignment_status: string;
    filterval: string;
    show_consignmentshipment: string;
}

export interface ConsignmentsCompanyChanged {
    stockplace_id_from: string;
}

export interface ConsignmentsCompanyParams {
    businessco_no_from: string;
}

export interface ConsignmentsStockplaceParams {
    stockplace_id_from: string;
}

export interface ConsignmentsStockplaceChanged {
    stocklocation_keyno_trolley: string;
    consignmentshipment_keyno: string;
}

export interface ConsignmentsListParams extends ConsignmentsParams {
    businessco_no_from: string;
    stockplace_id_from: string;
    stocklocation_keyno_trolley: string;
    date_fom: string;
    date_tom: string;
    gb_consignment_status: string;
    consignmentshipment_keyno: string;
}

export interface ConsignmentShipmentsParams {
    stockplace_id: string;
    add_all?: '1' | '0';
    add_none?: '1' | '0';
}

export interface ConsignmentGet extends Record<string, unknown>, ConsignmentAssignParams {
    bg_consignmentselection: string;
    bg_consignment_packing_selection: string;
    bg_ConsignmentPickSelection: string;
    floating_location: string;
    statusid: string;
    stockplace_id_from: string;
    stocklocation_keyno: string | number;
    stocktransfer_keyno: string | number;
    deliverymethod_no: string | number | null;
    consignmentshipment_keyno: string | number;
    cargonizer_printer_id: string | number;
    colli: string | number;
    weight: string | number;
    volum: string | number;
    length_cm: string | number;
    width_cm: string | number;
    height_cm: string | number;
    show_xyz: string;
    setting_use_colli_dimensions: string;
    send_delivery_confirmation: string;
    consignee_name: string;
    consignee_address1: string;
    consignee_address2: string;
    consignee_postcode: string;
    consignee_city: string;
    consignee_country: string;
    consignee_email: string;
    consignee_mobile: string;
    consignee_phone: string;
    consignee_contact_person: string;
    message_consignee: string;
    message_carrier: string;
    reference_consignee: string;
    reference_consignor: string;
    businessco_no: string;
    delivery_cust_no: string;
    consignee_customer_number: string;
    order_order_nos: string;
    order_internal_no_first: string | number;
    batchdelivery_keyno: string | number;
    tracking_url: string;
    waybill_pdf: string;
    cargonizer_consignment_id: string;
    consignmentcarriers: string;
    consignmentcarriers_warning: string;
    credit_information: string;
    picked_information: string;
    prod_id_freight: string;
    order_freight: string | number;
    est_freight: string | number;
    orderhead_heading: string;
    setting_show_orderhead_heading: string;
    setting_show_send_delivery_confirmation: string;
    consignment_keyno_splitfrom: string | number;
    nbr_of_consignmentsplit: string | number;
    nbr_of_documents: string | number;
    checklist_count: string | number;
    show_button_consignment_split: string;
    show_button_consignment_consignmentsplitedit: string;
    show_rfidconnect: string;
    show_consignmentlineselect: string;
    show_consignment_prepick_finished: string;
    show_consignmentcarriers: string;
    show_consignmentshipment: string;
    show_freight_fields: string;
    show_prod_id_freight: string;
    show_consignmentserialnumber: string;
    show_checklists: string;
    show_delete_consignment: string;
    show_button_rollback_delivery: string;
    show_setting: string;
}

export interface ConsignmentSaveResponse { saveok: string; }
export interface ConsignmentPickResponse { completed_ok: string; }
export interface ConsignmentPrevalidation { status_no: string; status_message: string; }
export interface ConsignmentDeliveryMethod {
    deliverymethod_no: string | number;
    deliverymethod_name: string;
    deliverymethod_cargonizer: string;
    send_delivery_confirmation: string;
}
export interface ConsignmentStockTransferParams { stocktransfer_keyno: string | number; }
export interface ConsignmentPrePickItem extends Item { special_product?: string; }
export interface ConsignmentColliItem extends Item { item_weight?: string | number; item_volume?: string | number; }
export interface ConsignmentColliListParams extends ConsignmentAssignParams { retreive_type: string; }
export interface ConsignmentColliActionParams extends ConsignmentAssignParams { data: ConsignmentColliItem[]; }
export interface ConsignmentFreightParams extends ConsignmentAssignParams {
    deliverymethod_no: string | number | null;
    colli: string | number;
    weight: string | number;
    volum: string | number;
}
export interface ConsignmentFreightResponse extends ErrorMessageResponse { estimated_cost: string | number; }
export interface ConsignmentApplyFreightParams extends ConsignmentAssignParams { prod_id_freight: string; est_freight: string | number; }
export interface ConsignmentOrderFreightParams extends ConsignmentAssignParams { prod_id: string; }
export interface ConsignmentOrderFreightResponse { sum_amount_net_locval: string | number; }
